import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listMyReviews } from "@/lib/review-moderation.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/bartender/reviews")({
  head: () => ({ meta: [{ title: "My reviews — The Hospitality Reserve" }] }),
  component: MyReviewsPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

type Review = { id: string; rating: number; comment: string | null; created_at: string };

function MyReviewsPage() {
  const load = useServerFn(listMyReviews);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await load();
      if ("error" in r) toast.error(r.error); else setReviews(r.reviews as Review[]);
      setLoading(false);
    })();
  }, []);

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-2xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Bartender</p>
        <h1 className="text-3xl font-light">My reviews</h1>
        {!loading && reviews.length > 0 && (
          <p className="mt-2 text-sm text-muted-foreground">
            Average rating <span className="text-foreground">{avg.toFixed(1)} ★</span> across {reviews.length} review{reviews.length === 1 ? "" : "s"}.
          </p>
        )}
        {loading ? <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        : reviews.length === 0 ? <p className="mt-6 text-sm text-muted-foreground">No reviews yet — they'll appear here as venues rate your engagements.</p>
        : (
          <ul className="mt-6 divide-y divide-border border border-border">
            {reviews.map((r) => (
              <li key={r.id} className="p-4">
                <p className="text-sm">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
                <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-GB")}</p>
                {r.comment && <p className="mt-2 text-sm">{r.comment}</p>}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
